import React from 'react';
import { useGetEconomicMetricsQuery } from 'store/services/economic/economicApi';
import MetricBox from './MetricBox';
import './EconomicMetricsDashboard.css';

/**
 * Economic Health Dashboard Component
 * Displays 8 key metrics that indicate the health of TradeMagic's trading economy
 * Shown at the top of the Sustainable Economics article page
 */
const EconomicMetricsDashboard: React.FC = () => {
  const { data, isLoading, isError, refetch } = useGetEconomicMetricsQuery();

  // Error state
  if (isError) {
    return (
      <div className="economic-dashboard">
        <div className="dashboard-header">
          <h2>Global Snapshot of TradeMagic</h2>
          <p>Metrics that we use to evaluate the health of the trading economy</p>
        </div>
        <div className="dashboard-error">
          <p>Unable to load economic metrics. Please try again later.</p>
          <button onClick={() => refetch()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="economic-dashboard">
      <div className="dashboard-header">
        <h2>Global Snapshot of TradeMagic</h2>
        <p>Metrics that we use to evaluate the health of the trading economy</p>
      </div>

      <div className="dashboard-grid">
        {/* Row 1 */}
        <MetricBox
          value={data?.tradesPerMonth ?? 0}
          label="Trades per month"
          type="number"
          isLoading={isLoading}
          tooltip="The total number of cards traded in the last 30 days"
        />
        <MetricBox
          value={data?.totalPointsInCirculation ?? 0}
          label="Total points in circulation"
          type="points"
          isLoading={isLoading}
          tooltip="All points currently held by users across the platform"
        />
        <MetricBox
          value={data?.activeTraders ?? 0}
          label="Active traders"
          type="number"
          isLoading={isLoading}
          tooltip="Users who completed at least one trade or created an account in the last 30 days"
        />
        <MetricBox
          value={data?.pointsPerActiveTrader ?? 0}
          label="Points per active trader"
          type="points"
          isLoading={isLoading}
          tooltip="Average point balance among active traders (total points ÷ active traders)"
        />

        {/* Row 2 */}
        <MetricBox
          value={data?.activeTraderDiscountPercentage ?? 0}
          label="Active trader discount"
          type="percentage"
          isLoading={isLoading}
          tooltip="A reduction in monthly fee for active users"
        />
        <MetricBox
          value={data?.monthlyPointFee ?? 0}
          label="Monthly point fee"
          type="points"
          isLoading={isLoading}
          tooltip="Fixed points deducted from all accounts each month"
        />
        <MetricBox
          value={data?.transactionFeePercentage ?? 0}
          label="Transaction fee"
          type="percentage"
          isLoading={isLoading}
          tooltip="Percentage of points deducted from the sender on each trade to remove points from circulation"
        />
        <MetricBox
          value={data?.targetPointsPerActiveTrader ?? 0}
          label="Target points per active trader"
          type="points"
          isLoading={isLoading}
          tooltip="The ideal average point balance to maintain healthy trading liquidity"
        />
      </div>
    </div>
  );
};

export default EconomicMetricsDashboard;
